import { useEffect, useMemo } from 'react';
import {
  BufferGeometry,
  Float32BufferAttribute,
  LineBasicMaterial,
  LineDashedMaterial,
  LineSegments,
  Material,
  Vector3,
} from 'three';
import {
  parseGCode,
  centerPoints,
  calculateBoundingBox,
  getGcodeUnit,
} from './utils.ts';
import BoundingBox from './BoundingBox.tsx';

interface GCodeRendererProps {
  gcode: string;
  modelColor: string;
  boundingBoxColor: string;
  dTextColor: string;
}

/** Flatten many polyline segments into ONE `LineSegments` — a single draw call
 * and a single scene object for the whole pass, rather than one drei `<Line>`
 * (a fat-line mesh + material + React node) per segment. A big program is
 * thousands of segments; the old way was thousands of draw calls that pinned
 * the main thread. Each polyline p0..pk expands to the consecutive vertex pairs
 * (p0,p1),(p1,p2)… that `LineSegments` draws. */
function buildLineSegments(
  segments: Vector3[][],
  material: Material
): LineSegments {
  const positions: number[] = [];
  segments.forEach((seg) => {
    for (let i = 0; i < seg.length - 1; i += 1) {
      const a = seg[i];
      const b = seg[i + 1];
      positions.push(a.x, a.y, a.z, b.x, b.y, b.z);
    }
  });
  const geometry = new BufferGeometry();
  geometry.setAttribute('position', new Float32BufferAttribute(positions, 3));
  return new LineSegments(geometry, material);
}

function GCodeRenderer({
  gcode,
  modelColor,
  boundingBoxColor,
  dTextColor,
}: GCodeRendererProps) {
  const { rapidSegments, linearSegments, rapidMovePoints, linearMovePoints } =
    useMemo(() => {
      return parseGCode(gcode);
    }, [gcode]);

  // Calculate bounding box and center points
  const { centeredLinearSegments, centeredRapidSegments, centeredBoundingBox } =
    useMemo(() => {
      const allPoints = [...rapidMovePoints, ...linearMovePoints];
      const boundingBox = calculateBoundingBox(allPoints);

      // Center the linear segments
      const centeredLinear = linearSegments.map((segment) =>
        centerPoints(segment, boundingBox)
      );

      // Center the rapid segments
      const centeredRapid = rapidSegments.map((segment) =>
        centerPoints(segment, boundingBox)
      );

      // Recalculate bounding box for centered points
      const allCenteredPoints = [
        ...centeredLinear.flat(),
        ...centeredRapid.flat(),
      ];
      const centeredBox = calculateBoundingBox(allCenteredPoints);

      return {
        centeredLinearSegments: centeredLinear,
        centeredRapidSegments: centeredRapid,
        centeredBoundingBox: centeredBox,
      };
    }, [rapidMovePoints, linearMovePoints, rapidSegments, linearSegments]);

  const gcodeMeasuringUnit = useMemo(() => getGcodeUnit(gcode), [gcode]);

  // Cutting paths (solid). Native line width is always 1px, which is what the
  // old `lineWidth={1}` drew anyway — the fat-line path bought nothing here.
  const cutting = useMemo(
    () =>
      buildLineSegments(
        centeredLinearSegments,
        new LineBasicMaterial({ color: modelColor })
      ),
    [centeredLinearSegments, modelColor]
  );

  // Rapid moves (dashed red). LineSegments.computeLineDistances resets the dash
  // per pair, so every rapid dashes consistently regardless of length.
  const rapid = useMemo(() => {
    const line = buildLineSegments(
      centeredRapidSegments,
      new LineDashedMaterial({ color: 'red', dashSize: 1, gapSize: 0.5 })
    );
    line.computeLineDistances();
    return line;
  }, [centeredRapidSegments]);

  // useMemo builds a fresh geometry+material each time its inputs change; the
  // previous ones leak GPU memory unless disposed.
  useEffect(
    () => () => {
      cutting.geometry.dispose();
      (cutting.material as Material).dispose();
    },
    [cutting]
  );
  useEffect(
    () => () => {
      rapid.geometry.dispose();
      (rapid.material as Material).dispose();
    },
    [rapid]
  );

  return (
    <>
      {/* eslint-disable-next-line react/no-unknown-property */}
      <primitive object={cutting} />
      {/* eslint-disable-next-line react/no-unknown-property */}
      <primitive object={rapid} />

      {centeredBoundingBox && (
        <BoundingBox
          box={centeredBoundingBox}
          boundingBoxColor={boundingBoxColor}
          dTextColor={dTextColor}
          unit={gcodeMeasuringUnit}
        />
      )}
    </>
  );
}

export default GCodeRenderer;
